import type { EventProperties } from '@/lib/analytics'

export type SavedPlanMode = 'identify' | 'diagnose' | 'weed'
export type SavedPlanType = 'care' | 'treatment' | 'monitoring' | 'control'
export type SavedPlanProvider = 'google' | 'apple'

export interface SavedPlanAnalyticsContext {
  analysisId?: string
  mode: SavedPlanMode
  planType: SavedPlanType
  outcome: string
  entryPath: string
  funnelId?: string
  subject?: string
}

export interface PendingSavedPlanFlow extends SavedPlanAnalyticsContext {
  saveFlowId: string
  method: 'oauth'
  provider: SavedPlanProvider
  startedAt: number
}

export interface PendingSavedPlanDestination {
  flow: PendingSavedPlanFlow
  destination: string
  action: 'primary' | 'secondary' | 'history'
  markedAt: number
}

export const SAVED_PLAN_FLOW_COOKIE = 'saved_plan_flow'
const SAVED_PLAN_FLOW_STORAGE_KEY = 'saved_plan_pending_flow'
const SAVED_PLAN_DESTINATION_STORAGE_KEY = 'saved_plan_pending_destination'
const SAVED_PLAN_FLOW_TTL_MS = 30 * 60 * 1000

function cleanString(value: unknown, maxLength = 160): string | undefined {
  if (typeof value !== 'string') return undefined
  const cleaned = value.trim()
  return cleaned ? cleaned.slice(0, maxLength) : undefined
}

function newSaveFlowId(): string {
  try {
    return crypto.randomUUID()
  } catch {
    return `save_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
  }
}

export function savedPlanEventProperties(
  context: SavedPlanAnalyticsContext,
  extra: EventProperties = {},
): EventProperties {
  return {
    schema_version: 1,
    analysis_id: context.analysisId,
    mode: context.mode,
    plan_type: context.planType,
    outcome: context.outcome,
    entry_path: context.entryPath,
    funnel_id: context.funnelId,
    subject: context.subject,
    ...extra,
  }
}

function parseFlowValue(value: unknown, now = Date.now()): PendingSavedPlanFlow | null {
  if (!value || typeof value !== 'object') return null
  const candidate = value as Partial<PendingSavedPlanFlow>
  const analysisId = cleanString(candidate.analysisId)
  const saveFlowId = cleanString(candidate.saveFlowId)
  const entryPath = cleanString(candidate.entryPath)
  const outcome = cleanString(candidate.outcome)
  const startedAt = typeof candidate.startedAt === 'number' && Number.isFinite(candidate.startedAt)
    ? candidate.startedAt
    : 0

  if (
    !saveFlowId ||
    !entryPath ||
    !outcome ||
    !startedAt ||
    now - startedAt < -2 * 60 * 1000 ||
    now - startedAt > SAVED_PLAN_FLOW_TTL_MS ||
    !['identify', 'diagnose', 'weed'].includes(candidate.mode || '') ||
    !['care', 'treatment', 'monitoring', 'control'].includes(candidate.planType || '') ||
    candidate.method !== 'oauth' ||
    !['google', 'apple'].includes(candidate.provider || '')
  ) {
    return null
  }

  return {
    analysisId,
    saveFlowId,
    mode: candidate.mode as SavedPlanMode,
    planType: candidate.planType as SavedPlanType,
    outcome,
    entryPath,
    funnelId: cleanString(candidate.funnelId),
    subject: cleanString(candidate.subject),
    method: 'oauth',
    provider: candidate.provider as SavedPlanProvider,
    startedAt,
  }
}

export function parseSavedPlanFlowCookie(
  raw: string | null | undefined,
  now = Date.now(),
): PendingSavedPlanFlow | null {
  if (!raw) return null
  try {
    return parseFlowValue(JSON.parse(decodeURIComponent(raw)), now)
  } catch {
    return null
  }
}

export function readPendingSavedPlanFlow(
  analysisId?: string,
  now = Date.now(),
): PendingSavedPlanFlow | null {
  if (typeof window === 'undefined') return null
  try {
    const parsed = parseFlowValue(
      JSON.parse(window.localStorage.getItem(SAVED_PLAN_FLOW_STORAGE_KEY) || 'null'),
      now,
    )
    if (!parsed) {
      window.localStorage.removeItem(SAVED_PLAN_FLOW_STORAGE_KEY)
      return null
    }
    if (analysisId && parsed.analysisId && parsed.analysisId !== analysisId) return null
    return parsed
  } catch {
    return null
  }
}

export function beginSavedPlanFlow(
  context: SavedPlanAnalyticsContext,
  provider: SavedPlanProvider,
  now = Date.now(),
): PendingSavedPlanFlow {
  const existing = readPendingSavedPlanFlow(context.analysisId, now)
  const flow: PendingSavedPlanFlow = {
    ...context,
    saveFlowId: existing?.saveFlowId || newSaveFlowId(),
    method: 'oauth',
    provider,
    startedAt: existing?.startedAt || now,
  }

  try {
    window.localStorage.setItem(SAVED_PLAN_FLOW_STORAGE_KEY, JSON.stringify(flow))
  } catch {
    // Attribution is best-effort when storage is unavailable.
  }

  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : ''
    document.cookie = `${SAVED_PLAN_FLOW_COOKIE}=${encodeURIComponent(JSON.stringify(flow))}; path=/; max-age=${Math.floor(SAVED_PLAN_FLOW_TTL_MS / 1000)}; SameSite=Lax${secure}`
  } catch {
    // The local flow still lets the destination and claim events join.
  }

  return flow
}

export function pendingSavedPlanEventProperties(
  context: SavedPlanAnalyticsContext,
  extra: EventProperties = {},
): EventProperties {
  const pending = readPendingSavedPlanFlow(context.analysisId)
  return savedPlanEventProperties(context, {
    save_flow_id: pending?.saveFlowId,
    method: pending?.method,
    provider: pending?.provider,
    ...extra,
  })
}

export function markSavedPlanDestination(
  context: SavedPlanAnalyticsContext,
  destination: string,
  action: PendingSavedPlanDestination['action'],
): void {
  const flow = readPendingSavedPlanFlow(context.analysisId)
  if (!flow) return
  try {
    window.sessionStorage.setItem(SAVED_PLAN_DESTINATION_STORAGE_KEY, JSON.stringify({
      flow,
      destination,
      action,
      markedAt: Date.now(),
    } satisfies PendingSavedPlanDestination))
  } catch {
    // Navigation tracking is best-effort.
  }
}

export function consumeSavedPlanDestination(
  expectedDestination?: string,
  now = Date.now(),
): PendingSavedPlanDestination | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(SAVED_PLAN_DESTINATION_STORAGE_KEY)
    if (!raw) return null
    const candidate = JSON.parse(raw) as Partial<PendingSavedPlanDestination>
    const flow = parseFlowValue(candidate.flow, now)
    const destination = cleanString(candidate.destination)
    const action = candidate.action
    const markedAt = typeof candidate.markedAt === 'number' ? candidate.markedAt : 0
    if (
      !flow ||
      !destination ||
      !['primary', 'secondary', 'history'].includes(action || '') ||
      !markedAt ||
      now - markedAt > SAVED_PLAN_FLOW_TTL_MS ||
      (expectedDestination && destination !== expectedDestination)
    ) {
      return null
    }
    window.sessionStorage.removeItem(SAVED_PLAN_DESTINATION_STORAGE_KEY)
    return {
      flow,
      destination,
      action: action as PendingSavedPlanDestination['action'],
      markedAt,
    }
  } catch {
    return null
  }
}
